/**
 * A quad in the world with a 2D canvas painted onto it.
 *
 * This is the whole trick behind the in-headset UI: DOM does not render inside
 * an immersive WebXR session, so every overlay the game has is redrawn onto a
 * texture and hung in the room instead.
 *
 * Two rules keep it cheap:
 *  - **Repaint on change, never per frame.** `paint()` is called by content
 *    code only when a value it draws actually moved, exactly as `ui/hud.ts`
 *    only writes to the DOM when a number changed.
 *  - **Nothing exists until an XR session does.** Panels are constructed by
 *    `layer.ts` on the first session and torn down with the layer.
 *
 * Legibility is the other constraint. A Quest 3 resolves roughly twenty pixels
 * per degree at the centre of the lens, so a panel is authored at enough canvas
 * resolution to supersample that (see `PIXELS_PER_METRE`) and text is sized in
 * *metres at the panel's design distance* rather than in pixels, so a panel can
 * be pulled closer to dodge a wall without its type getting bigger.
 */

import * as THREE from 'three';
import { clamp01 } from '../../core/math';
import type { XRRay } from './pointer';

/**
 * Canvas pixels per metre of panel.
 *
 * At the 1.25 m the content dock sits at, one degree of arc is about 21.8 mm,
 * so this is roughly 33 canvas pixels per degree — about 1.6x a Quest 3's
 * centre resolution. Enough that type is resolved rather than reconstructed,
 * and past the point where more would only cost upload bandwidth.
 */
export const PIXELS_PER_METRE = 1500;

/** Nothing in this UI needs more, and some mobile XR GPUs cap here. */
const MAX_PIXELS = 2048;

/** Shared by every panel; sized per mesh through `scale`. */
function quad(): THREE.PlaneGeometry {
  return new THREE.PlaneGeometry(1, 1);
}

export class CanvasPanel {
  readonly object: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;

  /** Canvas size in pixels. Content code lays out in these. */
  readonly width: number;
  readonly height: number;

  private readonly canvas: HTMLCanvasElement;
  private readonly c2d: CanvasRenderingContext2D | null;
  private readonly texture: THREE.CanvasTexture;
  private readonly pixelsPerMetre: number;

  /**
   * @param widthM  Panel width in metres at unit dock scale.
   * @param heightM Panel height in metres at unit dock scale.
   */
  constructor(widthM: number, heightM: number, ppm = PIXELS_PER_METRE) {
    const longest = Math.max(widthM, heightM) * ppm;
    const scale = longest > MAX_PIXELS ? MAX_PIXELS / longest : 1;
    this.pixelsPerMetre = ppm * scale;
    this.width = even(widthM * this.pixelsPerMetre);
    this.height = even(heightM * this.pixelsPerMetre);

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.c2d = this.canvas.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = true;

    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      // Panels overlap each other and are drawn back to front by `renderOrder`;
      // writing depth would make whichever landed first punch a hole in the
      // rest. They are still depth-*tested*, which is what stops a panel from
      // being drawn through the wall it has been pulled in front of.
      depthWrite: false,
      // The scene's fog is what carries distance in this game. A panel a metre
      // from the face is not at a distance, and fogging it would tint the amber
      // toward whatever colour the floor happens to be.
      fog: false,
      // ACES has a shoulder on it that would pull the amber off the value the
      // rest of the UI uses. UI is authored in sRGB and shown in sRGB.
      toneMapped: false,
      side: THREE.DoubleSide,
    });

    this.object = new THREE.Mesh(quad(), material);
    this.object.scale.set(widthM, heightM, 1);
    // Panels are placed by hand every frame and are never worth a bounding
    // sphere test, which would also cull them wrongly the frame they move.
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = true;
    this.object.visible = false;
  }

  /** Panel metres -> canvas pixels. Type is authored in metres; see the header. */
  px(metres: number): number {
    return metres * this.pixelsPerMetre;
  }

  /**
   * Repaint. The callback gets a context already reset to the identity
   * transform, plus the canvas size in pixels. The upload happens here, once.
   */
  paint(draw: (c: CanvasRenderingContext2D, w: number, h: number) => void): void {
    const c = this.c2d;
    if (!c) return;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.globalAlpha = 1;
    draw(c, this.width, this.height);
    this.texture.needsUpdate = true;
  }

  setVisible(v: boolean): void {
    this.object.visible = v;
  }

  get visible(): boolean {
    return this.object.visible;
  }

  /** 0..1. Used to fade panels in and out rather than popping them. */
  setOpacity(a: number): void {
    this.object.material.opacity = a;
    this.object.visible = a > 0.004;
  }

  /** Later panels composite over earlier ones. */
  setOrder(order: number): void {
    this.object.renderOrder = order;
  }

  dispose(): void {
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.object.material.dispose();
    this.texture.dispose();
  }
}

/**
 * A quad showing a canvas somebody else owns and paints.
 *
 * This exists for the map, which already renders a perfectly good sheet of
 * paper into a canvas in `ui/map.ts`. Reimplementing that for the headset would
 * be a second map that had to be kept in step with the first; hanging its own
 * canvas on a wall is free and cannot drift.
 */
export class MirrorPanel {
  readonly object: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly texture: THREE.CanvasTexture;

  constructor(source: HTMLCanvasElement, widthM: number, heightM: number) {
    this.texture = new THREE.CanvasTexture(source);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = true;

    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.object = new THREE.Mesh(quad(), material);
    this.object.scale.set(widthM, heightM, 1);
    this.object.frustumCulled = false;
    this.object.visible = false;
  }

  /**
   * Re-upload the source canvas.
   *
   * Called on a throttle rather than every frame: the map is a full-canvas
   * texture upload, and the sheet only changes when the player crosses a cell
   * or pans it — which, since the full map halts the simulation, is almost
   * never while it is being looked at.
   */
  refresh(): void {
    this.texture.needsUpdate = true;
  }

  setSize(widthM: number, heightM: number): void {
    this.object.scale.set(widthM, heightM, 1);
  }

  setVisible(v: boolean): void {
    this.object.visible = v;
  }

  setOpacity(a: number): void {
    this.object.material.opacity = a;
    this.object.visible = a > 0.004;
  }

  setOrder(order: number): void {
    this.object.renderOrder = order;
  }

  dispose(): void {
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.object.material.dispose();
    this.texture.dispose();
  }
}

function even(v: number): number {
  return Math.max(2, Math.round(v / 2) * 2);
}

/* ------------------------------------------------------------------ *
 * Pointing at a panel
 * ------------------------------------------------------------------ */

/** Where a ray landed on a quad, in the panel's own canvas coordinates. */
export interface QuadHit {
  /** 0..1 from the left edge. */
  u: number;
  /** 0..1 from the top edge, so it reads the same way the canvas does. */
  v: number;
  /** Metres from the ray's origin to the panel. Picks between overlaps. */
  distance: number;
}

/** Reused by every hit test; the layer runs a handful of these a frame. */
const _inverse = new THREE.Matrix4();
const _localOrigin = new THREE.Vector3();
const _localDir = new THREE.Vector3();
const _localHit = new THREE.Vector3();

/**
 * Where a world-space ray crosses a panel, or null if it misses.
 *
 * `margin` is metres of slop allowed *outside* the quad, and it is the whole
 * reason this takes one. A gaze ray is far less precise than a mouse and the
 * player cannot see where it is pointing until after they have pinched, so
 * every target in here is quietly larger than it looks. A hit inside the margin
 * is clamped to the edge rather than reported as outside, which means the edge
 * rows of a menu are as easy to press as the middle ones.
 *
 * The quad is assumed to be `PlaneGeometry(1, 1)` scaled to size — every panel
 * in `ui/xr/` is — so the local hit lands in ±0.5 and converts straight to a
 * fraction of the canvas.
 */
export function hitQuad(object: THREE.Object3D, ray: XRRay, margin = 0): QuadHit | null {
  if (!object.visible) return null;
  _inverse.copy(object.matrixWorld).invert();
  _localOrigin.copy(ray.origin).applyMatrix4(_inverse);
  // Direction only: `transformDirection` drops the translation and normalises,
  // so `t` below is in the panel's own units rather than in metres. The metres
  // are recovered at the end, from the world-space point.
  _localDir.copy(ray.direction).transformDirection(_inverse);
  if (Math.abs(_localDir.z) < 1e-6) return null;

  const t = -_localOrigin.z / _localDir.z;
  // Behind the eye, or behind the panel: not a hit either way.
  if (t <= 0) return null;
  const x = _localOrigin.x + _localDir.x * t;
  const y = _localOrigin.y + _localDir.y * t;

  // The margin is quoted in metres and the quad is in fractions of itself, so
  // it has to be divided by the panel's world size to mean the same thing on a
  // wide panel and a narrow one.
  const e = object.matrixWorld.elements;
  const sx = Math.hypot(e[0]!, e[1]!, e[2]!);
  const sy = Math.hypot(e[4]!, e[5]!, e[6]!);
  const mx = sx > 1e-6 ? margin / sx : 0;
  const my = sy > 1e-6 ? margin / sy : 0;
  if (Math.abs(x) > 0.5 + mx || Math.abs(y) > 0.5 + my) return null;

  _localHit.set(x, y, 0).applyMatrix4(object.matrixWorld);
  return {
    u: clamp01(x + 0.5),
    // Canvas y runs down the panel and the quad's runs up it.
    v: clamp01(0.5 - y),
    distance: _localHit.distanceTo(ray.origin),
  };
}

/* ------------------------------------------------------------------ *
 * Press feedback
 * ------------------------------------------------------------------ */

/** Seconds the ring takes to expand and go. Short: it is a receipt, not an effect. */
const MARK_LIFE = 0.34;
/** Metres the ring floats in front of the panel it is marking. */
const MARK_LIFT = 0.004;

const _markAt = new THREE.Vector3();

/**
 * A ring that blooms where a press landed.
 *
 * This exists because of the one thing a gaze-and-pinch headset takes away:
 * there is no cursor, so until the player has pressed something they have no
 * idea where their ray is going, and if a press does the wrong thing they
 * cannot tell whether they mis-aimed or mis-read. A mark at the exact landing
 * point is how somebody learns to aim a pointer they cannot see.
 *
 * It is a mesh rather than something painted into the panel's canvas on
 * purpose. Animating it in the canvas would mean re-uploading a megapixel
 * texture every frame for a third of a second; as geometry it is a transform
 * and an opacity, and it costs one draw call while it is alive.
 */
export class PressMark {
  readonly object: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  /** 1 at the press, 0 once it has gone. */
  private life = 0;

  constructor(colour = 0xe8d98a) {
    const geometry = new THREE.RingGeometry(0.009, 0.014, 24);
    const material = new THREE.MeshBasicMaterial({
      color: colour,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    });
    this.object = new THREE.Mesh(geometry, material);
    this.object.frustumCulled = false;
    this.object.visible = false;
    this.object.renderOrder = 1036;
  }

  /**
   * Put the mark on `panel` at the given canvas fraction.
   *
   * It is parented to the panel's *parent* rather than to the panel, because a
   * panel carries its size in its scale and a ring inheriting that would come
   * out as an ellipse. The dock above it is scaled uniformly, so the ring keeps
   * both its shape and its angular size.
   */
  show(panel: THREE.Object3D, u: number, v: number): void {
    const parent = panel.parent;
    if (!parent) return;
    parent.add(this.object);
    _markAt.set(u - 0.5, 0.5 - v, MARK_LIFT).applyMatrix4(panel.matrix);
    this.object.position.copy(_markAt);
    this.object.quaternion.copy(panel.quaternion);
    this.life = 1;
    this.object.visible = true;
  }

  /** `dt` is wall clock: the mark has to fade while the simulation is halted. */
  update(dt: number): void {
    if (this.life <= 0) return;
    this.life = Math.max(0, this.life - dt / MARK_LIFE);
    const k = 1 - this.life;
    this.object.scale.setScalar(0.75 + k * 1.4);
    // Squared, so it is bright for the moment that answers "did that register?"
    // and then leaves quickly.
    this.object.material.opacity = this.life * this.life * 0.95;
    this.object.visible = this.life > 0;
  }

  /** Take it out of whatever dock it was left on. */
  detach(): void {
    this.life = 0;
    this.object.visible = false;
    this.object.removeFromParent();
  }

  dispose(): void {
    this.detach();
    this.object.geometry.dispose();
    this.object.material.dispose();
  }
}
